export default class UndistractorOptionStore {
  static instance = null;

  // Standard Singleton pattern access point
  static getInstance() {
    if (!UndistractorOptionStore.instance) {
      UndistractorOptionStore.instance = new UndistractorOptionStore();
    }
    return UndistractorOptionStore.instance;
  }

  constructor() {
    if (UndistractorOptionStore.instance) {
      return UndistractorOptionStore.instance;
    }

    this.undistractor_options = new Map();
    this.subscribers = new Map();
    this.globalSubscribers = new Set();
    this.extensionSynced = false;

    // Safety: Load on DOM ready/Turbo load in case the script tag wasn't parsed yet
    // when the Store was first instantiated.
    ['turbo:load', 'DOMContentLoaded'].forEach((event) => {
      document.addEventListener(event, () => this.loadFromElementID('undistractor-options-data'));
    });

    UndistractorOptionStore.instance = this;
  }

  // ----------------------------------------------------------------
  // WRITE METHODS
  // ----------------------------------------------------------------

  // Update or Insert multiple records
  // Updates multiple records and triggers only one global notification
  upsertAll(records, skipExtensionSync = false) {
    const updatedRecords = [];

    records.forEach((record) => {
      const id = record.id;
      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('UndistractorOptionStore: Cannot upsert record without an ID', record);
        return;
      }

      const normalizedId = parseInt(id, 10);
      const existing = this.undistractor_options.get(normalizedId) || {};

      // Merge existing data with new data
      const updated = { ...existing, ...record, id: normalizedId };

      // Sanitize schedule: convert 'null' string or null to empty string
      if (updated.schedule === 'null' || updated.schedule === null) {
        updated.schedule = '';
      }

      this.undistractor_options.set(normalizedId, updated);
      updatedRecords.push(updated);
    });

    this.#notifySubscribers(updatedRecords);
    this.#notifyGlobalSubscribers(updatedRecords);

    if (!skipExtensionSync) {
      this.#dispatchExtensionUpdates(updatedRecords);
    }
  }

  // Update or Insert a single record
  // Merges new data with existing data to ensure we don't lose state
  upsert(record, skipExtensionSync = false) {
    this.upsertAll([record], skipExtensionSync);
  }

  // Initialize/Load data from a DOM element ID (expects JSON content)
  loadFromElementID(elementId) {
    const element = document.getElementById(elementId);
    if (element && element.textContent) {
      try {
        const data = JSON.parse(element.textContent);
        this.loadFromServer(data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`UndistractorOptionStore: Failed to parse JSON from #${elementId}`, e);
      }
    }
  }

  // Load records from the extension (skips syncing back to extension). An empty list is still an answer —
  // signed out, it's what tells the settings table to stop waiting for the sync (UNS-5372).
  loadFromExtension(records) {
    this.extensionSynced = true;
    if (this.#hasRecords(records)) {
      this.upsertAll(records, true);
    } else {
      this.globalSubscribers.forEach((callback) => callback(this.getAll(), []));
    }
  }

  loadFromServer(records) {
    if (this.#hasRecords(records)) this.upsertAll(records, true);
  }

  // ----------------------------------------------------------------
  // READ METHODS
  // ----------------------------------------------------------------

  get(id) {
    const record = this.undistractor_options.get(parseInt(id, 10));
    return record ? { ...record } : undefined;
  }

  getAll() {
    return Array.from(this.undistractor_options.values()).sort(this.#sortComparator);
  }

  // ----------------------------------------------------------------
  // SUBSCRIPTION METHODS
  // ----------------------------------------------------------------

  // Subscribe to changes for a specific ID
  // callback: (data) => { ... }
  subscribe(id, callback) {
    const normalizedId = parseInt(id, 10);
    if (!this.subscribers.has(normalizedId)) {
      this.subscribers.set(normalizedId, new Set());
    }
    this.subscribers.get(normalizedId).add(callback);

    // Immediately call with current value if it exists
    const current = this.get(normalizedId);
    if (current) {
      callback(current);
    }

    // Return unsubscribe function for convenience
    return () => this.unsubscribe(normalizedId, callback);
  }

  // Subscribe to ALL changes (e.g. for Sidebar)
  // callback: (allData, changedItems) => { ... }
  subscribeAll(callback) {
    this.globalSubscribers.add(callback);

    callback(this.getAll());

    return () => this.unsubscribeAll(callback);
  }

  unsubscribe(id, callback) {
    const normalizedId = parseInt(id, 10);
    if (this.subscribers.has(normalizedId)) {
      this.subscribers.get(normalizedId).delete(callback);
      // Cleanup empty sets to prevent memory leaks
      if (this.subscribers.get(normalizedId).size === 0) {
        this.subscribers.delete(normalizedId);
      }
    }
  }

  unsubscribeAll(callback) {
    this.globalSubscribers.delete(callback);
  }

  // ----------------------------------------------------------------
  // PRIVATE METHODS
  // ----------------------------------------------------------------

  #hasRecords(records) {
    return Array.isArray(records) && records.length > 0;
  }

  #notifySubscribers(updatedRecords) {
    updatedRecords.forEach((updated) => {
      if (this.subscribers.has(updated.id)) {
        this.subscribers.get(updated.id).forEach((callback) => callback(updated));
      }
    });
  }

  #notifyGlobalSubscribers(updatedRecords) {
    if (updatedRecords.length > 0) {
      const allData = this.getAll();
      this.globalSubscribers.forEach((callback) => callback(allData, updatedRecords));
    }
  }

  #dispatchExtensionUpdates(updatedRecords) {
    if (updatedRecords.length === 0) return;

    const updates = updatedRecords.map((updated) => ({
      key: updated.key,
      schedule: updated.schedule || null,
      active: updated.active || false,
    }));

    const event = new CustomEvent('updateExtensionValues', {
      bubbles: true,
      detail: updates,
    });
    document.dispatchEvent(event);
  }

  #sortComparator(a, b) {
    // 1. Active items first
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }

    // 2. Sort by undistractor_position (undistractor type order)
    const aPosition = a.undistractor_position ?? Infinity;
    const bPosition = b.undistractor_position ?? Infinity;
    if (aPosition !== bPosition) {
      return aPosition - bPosition;
    }

    // 3. Sort by time_saved descending (nulls last)
    const aTimeSaved = a.time_saved ?? -Infinity;
    const bTimeSaved = b.time_saved ?? -Infinity;
    if (aTimeSaved !== bTimeSaved) {
      return bTimeSaved - aTimeSaved;
    }

    // 4. Sort by undistractable_name value
    return (a.undistractable_name || '').localeCompare(b.undistractable_name || '');
  }
}
